import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { captureReferralCode, getStoredReferralCode } from "@/lib/referral";
import SEOHead from "@/components/SEOHead";
import JsonLd, { softwareApplicationSchema } from "@/components/seo/JsonLd";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { HeroSection } from "@/components/landing/HeroSection";
import { ProblemSection } from "@/components/landing/ProblemSection";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { CustomerAppSection } from "@/components/landing/CustomerAppSection";
import { PaymentsSection } from "@/components/landing/PaymentsSection";
import { CompareTable } from "@/components/landing/CompareTable";
import { PricingSection } from "@/components/landing/PricingSection";
import { PartnerLeadCapture } from "@/components/landing/PartnerLeadCapture";
import { FAQSection } from "@/components/landing/FAQSection";
import { BlogTeaser } from "@/components/landing/BlogTeaser";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { GradientHairline } from "@/components/landing/BackgroundFX";

const Index = () => {
  const { t } = useTranslation("landing");

  // Second chance to capture a partner referral. The flyer's QR sends people
  // here through /r/{code}, which already writes the code to localStorage —
  // but that write is the single point of failure for the whole acquisition
  // channel. Private browsing, a locked-down in-app webview, or storage
  // pressure on an old phone and the partner's commission is gone silently,
  // with the code still sitting in plain sight in the address bar.
  //
  // captureReferralCode is first-touch, so re-running it on an existing,
  // unexpired capture is a no-op. Cheap insurance on the one hop that matters.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) captureReferralCode(ref);
  }, [searchParams]);

  // El bloque de captura sólo existe para quien llegó por un flyer. Se lee
  // DESPUES del efecto de arriba (la URL manda, el almacenamiento es el
  // respaldo) para que un ?ref= recien capturado ya cuente en este render.
  const referralCode = searchParams.get("ref") ?? getStoredReferralCode();

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      <SEOHead
        title={t("meta.title")}
        description={t("meta.description")}
        path="/"
        keywords={t("meta.keywords")}
      />
      <JsonLd data={softwareApplicationSchema()} />
      <LandingHeader />
      <main className="relative">
        <HeroSection />
        <GradientHairline />
        <ProblemSection />
        <GradientHairline />
        <HowItWorks />
        <GradientHairline />
        <CustomerAppSection />
        <GradientHairline />
        <PaymentsSection />
        <GradientHairline />
        <PricingSection />
        <GradientHairline />
        {/* Despues del precio a proposito: quien ya decidio subio a por el CTA
            del plan; esto recoge al que no, que hasta ahora se iba sin dejar
            forma de llamarlo (techalphamatrix/projects#371). */}
        {referralCode && (
          <>
            <PartnerLeadCapture code={referralCode} />
            <GradientHairline />
          </>
        )}
        <CompareTable />
        <GradientHairline />
        <FAQSection />
        <GradientHairline />
        <FinalCTA />
        <GradientHairline />
        <BlogTeaser />
      </main>
      <LandingFooter />
    </div>
  );
};

export default Index;
